import type { FC, ReactNode } from 'react'
import type { Category } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toCssColor } from '@/lib/colors'
import { pluralize } from '@/lib/format'

interface CategoryCardProps {
    category: Category
    showDescription?: boolean
    sizes?: string
    className?: string
}

export const CategoryCard: FC<CategoryCardProps> = ({
    category,
    showDescription = false,
    sizes = '(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw',
    className
}): ReactNode => {
    return (
        <Link
            href={`/categories/${category.slug}`}
            className={cn('group relative block overflow-hidden rounded-2xl ring-1 ring-white/[0.06] focus-visible:ring-3 focus-visible:ring-ring/70 focus-visible:outline-none', className)}
            style={{ backgroundColor: toCssColor(category.cover_color) }}
        >
            <div className='relative aspect-[4/3] w-full'>
                {category.cover_image ? (
                    <Image
                        src={category.cover_image}
                        alt={category.cover_alt || ''}
                        fill
                        sizes={sizes}
                        className='object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                    />
                ) : (
                    <div aria-hidden='true' className='absolute inset-0 bg-linear-to-br from-white/[0.08] to-transparent' />
                )}

                <div aria-hidden='true' className='absolute inset-0 bg-linear-to-t from-black/85 via-black/25 to-transparent' />

                <span className='absolute top-3 right-3 inline-flex size-9 items-center justify-center rounded-full bg-black/35 text-white opacity-0 backdrop-blur-md transition group-hover:opacity-100 group-focus-visible:opacity-100'>
                    <ArrowUpRight aria-hidden='true' className='size-4' />
                </span>

                <div className='absolute inset-x-0 bottom-0 p-4 sm:p-5'>
                    <h3
                        className='font-heading text-2xl leading-tight text-white sm:text-3xl'
                        data-mdlfy-cms={`categories:${category.id}:name`}
                    >
                        {category.name}
                    </h3>

                    <p className='mt-1 text-xs font-medium text-white/70'>
                        <span>{category.wallpaper_count}</span>
                        {' '}
                        {pluralize(category.wallpaper_count, 'wallpaper')}
                    </p>

                    {showDescription && category.description && (
                        <p
                            className='mt-2 line-clamp-2 text-sm leading-relaxed text-white/80'
                            data-mdlfy-cms={`categories:${category.id}:description`}
                        >
                            {category.description}
                        </p>
                    )}
                </div>
            </div>
        </Link>
    )
}
